// src/tabs/TaxeParcPanel.jsx — la taxe de séjour de TOUT le parc, d'un coup.
//
// Une ligne par logement, groupée par commune : ce qui est appliqué
// aujourd'hui, D'OÙ il vient, et ce qui manque. AUCUN montant n'est proposé
// ni pré-rempli — les tarifs viennent des délibérations et de l'espace
// hébergeur de Dimitri.
//
// SAISIE GROUPÉE (29/09, point 12 — CHEMIN ARGENT) : par commune, un seul
// formulaire vide, les logements concernés cochés, puis une CONFIRMATION qui
// dit que le prix en ligne de ces logements change. Rien ne change tant que
// Dimitri n'a pas saisi et enregistré lui-même.
//
// CATALOGUE (décision Dimitri 29/09, « les deux ») : une fois la configuration
// des logements enregistrée — et seulement alors — le même barème est écrit au
// catalogue `taxe_sejour_baremes` de la commune du groupe, par l'action
// qu'utilise déjà la fiche logement (`taxe_bareme_validate`). Un échec du
// catalogue est annoncé comme tel, jamais comme un succès complet.
//
// DA : primitives _paramsUi + tokens DS existants, comme CompletudePanel.

import { useCallback, useMemo, useRef, useState } from "react"
import { Landmark } from "lucide-react"
import { DS } from "../lib/ui"
import { Section, FicheAlert, StatusPill } from "./_paramsUi"
import { apercuTaxeParc, grouperParCommune, compterTaxeParc } from "../lib/taxe-parc"
import { validerSaisie, preparerEnregistrement, corpsCatalogue, origineBareme } from "../lib/taxe-saisie"
import { validateTaxeBareme } from "../lib/taxe-api"
import { getAllLogsFlat } from "../lib/sousloc-helpers"

const fmtNombre = (n) => String(n).replace(".", ",")

function LigneLogement({ l, origine }) {
  return (
    <div
      data-testid={`taxe-parc-${l.id}`}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
        padding: "8px 0", borderTop: `0.5px solid ${DS.border}`, fontSize: 13,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: DS.textPrimary }}>{l.nom}</div>
        {l.proprio && <div style={{ fontSize: 11.5, color: DS.textMuted }}>{l.proprio}</div>}
      </div>
      {l.bareme ? (
        <StatusPill tone="success" title={l.bareme.source === "global" ? "Barème général de la configuration" : "Barème propre au logement"}>
          {fmtNombre(l.bareme.taux_pct)} % · plafond {fmtNombre(l.bareme.plafond_eur)} €
          {l.bareme.source === "global" ? " (général)" : ""}
        </StatusPill>
      ) : (
        <StatusPill tone="danger">Aucun barème — pas de devis en direct</StatusPill>
      )}
      {origine && (
        <div data-testid={`taxe-parc-origine-${l.id}`} style={{ width: "100%", fontSize: 11, color: origine.code === "non_verifie" ? DS.warningText : DS.textMuted }}>
          {origine.texte}
        </div>
      )}
    </div>
  )
}

const VIDE = { commune: "", annee: "", taux: "", plafond: "", mineurs: "", source: "" }

function champ(label, value, onChange, props = {}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: DS.textSecondary, minWidth: 110, flex: 1 }}>
      {label}
      <input value={value} onChange={(e) => onChange(e.target.value)} {...props}
        style={{ fontSize: 13, padding: "6px 8px", border: `0.5px solid ${DS.border}`, borderRadius: DS.radiusControl, fontFamily: DS.fontBody }} />
    </label>
  )
}

// Écrit le catalogue, une ligne par code postal, par l'action de la fiche
// logement. Rend null si tout est écrit, sinon la raison (jamais d'exception).
async function ecrireCatalogue(prep) {
  if (!prep.ok) return prep.raison
  for (const corps of prep.corps) {
    try {
      const r = await validateTaxeBareme(corps)
      if (!r || r.ok !== true) return `${prep.corps.length > 1 ? `${corps.code_postal} : ` : ""}${(r && r.error) || "réponse inattendue du serveur"}`
    } catch (e) {
      console.error("[TaxeParcPanel] catalogue", e)
      return `${prep.corps.length > 1 ? `${corps.code_postal} : ` : ""}${e.message || e}`
    }
  }
  return null
}

// `verrou` est partagé par TOUS les formulaires de l'écran : chaque save
// réécrit la configuration ENTIÈRE, calculée sur celle que ce formulaire a en
// main. Deux envois simultanés (deux communes) : le second partirait d'une
// configuration qui ignore le premier et l'effacerait. Un envoi à la fois.
function SaisieCommune({ groupe, cfg, save, par, verrou }) {
  const [ouvert, setOuvert] = useState(false)
  // La commune est lue sur l'adresse (ce n'est pas un montant) ; tout le reste est VIDE.
  const saisieVide = () => ({ ...VIDE, commune: groupe.commune || "" })
  const [s, setS] = useState(saisieVide)
  const [coches, setCoches] = useState(() => new Set(groupe.logements.map((l) => l.id)))
  const [etape, setEtape] = useState("saisie") // saisie | confirmer | envoi
  const [message, setMessage] = useState(null)
  const v = validerSaisie(s)
  const maj = (k) => (val) => { setS((x) => ({ ...x, [k]: val })); setEtape("saisie"); setMessage(null) }
  // Seules comptent les cases cochées ENCORE PRÉSENTES dans la commune.
  const cochesPresents = groupe.logements.filter((l) => coches.has(l.id))
  const noms = cochesPresents.map((l) => l.nom)
  const nbCoches = cochesPresents.length
  const autreEnvoi = verrou.occupe && etape !== "envoi"

  const enregistrer = async () => {
    if (!verrou.prendre()) return
    setEtape("envoi"); setMessage(null)
    try {
      const prep = preparerEnregistrement(cfg, {
        idsCommune: groupe.logements.map((l) => l.id), coches, bareme: v.bareme, par,
      })
      if (!prep.ok) throw new Error(prep.erreur)
      await save(prep.cfg)
      const n = prep.touches.length
      const surN = `${n} logement${n > 1 ? "s" : ""}`
      // Les logements d'abord (ci-dessus) ; le catalogue de la commune SEULEMENT
      // s'ils ont réussi. Un échec du catalogue ne défait pas les logements,
      // mais il est dit : jamais un succès complet annoncé à tort.
      const raisonCatalogue = await ecrireCatalogue(
        corpsCatalogue({ commune: groupe.commune, codesPostaux: cochesPresents.map((l) => l.code_postal), bareme: v.bareme }),
      )
      setMessage(raisonCatalogue
        ? { ok: false, texte: `Barème enregistré sur ${surN}, mais le catalogue de la commune n'a pas été mis à jour : ${raisonCatalogue}` }
        : { ok: true, texte: `Enregistré pour ${surN} et au catalogue de ${groupe.commune}.` })
      // Enregistré : la saisie repart de zéro — rien ne reste pré-rempli pour la prochaine fois.
      setS(saisieVide()); setCoches(new Set(groupe.logements.map((l) => l.id)))
      setEtape("saisie"); setOuvert(false)
    } catch (e) {
      setMessage({ ok: false, texte: `Rien n'est enregistré : ${e.message || e}` })
      setEtape("confirmer")
    } finally {
      verrou.rendre()
    }
  }

  if (!save) return null
  if (!ouvert) {
    return (
      <div>
        <button type="button" data-testid={`taxe-saisir-${groupe.cle}`} onClick={() => setOuvert(true)}
          style={{ marginTop: 6, fontSize: 12, fontWeight: 700, border: `0.5px solid ${DS.border}`, borderRadius: DS.radiusControl, padding: "5px 12px", background: DS.card, color: DS.marine, cursor: "pointer" }}>
          Saisir le barème de cette commune
        </button>
        {message && <div data-testid="taxe-saisie-message" style={{ fontSize: 12, fontWeight: 700, marginTop: 6, color: message.ok ? DS.successText : DS.dangerText }}>{message.texte}</div>}
      </div>
    )
  }
  return (
    <div data-testid={`taxe-formulaire-${groupe.cle}`} style={{ marginTop: 8, padding: 12, border: `0.5px solid ${DS.border}`, borderRadius: 10, background: DS.card }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {champ("Commune", s.commune, maj("commune"), { "data-testid": "taxe-commune" })}
        {champ("Année du barème", s.annee, maj("annee"), { inputMode: "numeric", placeholder: "ex. l'année de la délibération", "data-testid": "taxe-annee" })}
        {champ("Taux (%)", s.taux, maj("taux"), { inputMode: "decimal", "data-testid": "taxe-taux" })}
        {champ("Plafond par nuit et par personne (€)", s.plafond, maj("plafond"), { inputMode: "decimal", "data-testid": "taxe-plafond" })}
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 8, fontSize: 12.5 }}>
        <span style={{ color: DS.textSecondary }}>Mineurs exonérés ?</span>
        {["oui", "non"].map((o) => (
          <label key={o} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            <input type="radio" name={`mineurs-${groupe.cle}`} checked={s.mineurs === o} onChange={() => maj("mineurs")(o)} data-testid={`taxe-mineurs-${o}`} /> {o}
          </label>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>{champ("Source (délibération, lien de l'espace hébergeur…)", s.source, maj("source"), { "data-testid": "taxe-source" })}</div>
      <div style={{ marginTop: 10, fontSize: 12, color: DS.textSecondary }}>Logements concernés :</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
        {groupe.logements.map((l) => (
          <label key={l.id} style={{ display: "inline-flex", gap: 4, alignItems: "center", fontSize: 12.5 }}>
            <input type="checkbox" checked={coches.has(l.id)} data-testid={`taxe-coche-${l.id}`}
              onChange={() => { setCoches((c) => { const n = new Set(c); if (n.has(l.id)) n.delete(l.id); else n.add(l.id); return n }); setEtape("saisie") }} />
            {l.nom}
          </label>
        ))}
      </div>
      {autreEnvoi && <div data-testid="taxe-autre-envoi" style={{ marginTop: 8, fontSize: 11.5, color: DS.textMuted }}>Un autre barème est en cours d'enregistrement — attends qu'il soit fini.</div>}
      {!v.ok && <div data-testid="taxe-erreurs" style={{ marginTop: 8, fontSize: 11.5, color: DS.textMuted }}>À compléter : {v.erreurs.join(" · ")}.</div>}
      {etape === "confirmer" && v.ok && (
        <FicheAlert tone="warning" style={{ marginTop: 10 }} testId="taxe-confirmation">
          Le prix payé en ligne va changer pour {noms.join(", ")}
          {groupe.commune ? `, et le catalogue de ${groupe.commune} sera mis à jour` : " (commune inconnue : le catalogue ne sera pas mis à jour)"} : taxe de séjour à {String(v.bareme.taux_pct).replace(".", ",")} %,
          plafond {String(v.bareme.plafond_eur).replace(".", ",")} € par nuit et par personne, mineurs {v.bareme.mineurs_exoneres ? "exonérés" : "non exonérés"},
          commune {v.bareme.commune}, barème {v.bareme.annee}.
        </FicheAlert>
      )}
      {message && <div data-testid="taxe-saisie-message" style={{ fontSize: 12, fontWeight: 700, marginTop: 6, color: message.ok ? DS.successText : DS.dangerText }}>{message.texte}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" data-testid="taxe-enregistrer" disabled={!v.ok || nbCoches === 0 || etape === "envoi" || verrou.occupe}
          onClick={() => (etape === "confirmer" ? enregistrer() : setEtape("confirmer"))}
          style={{ fontSize: 12.5, fontWeight: 700, border: "none", borderRadius: DS.radiusControl, padding: "7px 14px",
            background: etape === "confirmer" ? DS.danger : DS.marine, color: "#fff", cursor: v.ok && nbCoches && !verrou.occupe ? "pointer" : "not-allowed", opacity: v.ok && nbCoches && !autreEnvoi ? 1 : 0.5 }}>
          {etape === "envoi" ? "Enregistrement…" : etape === "confirmer" ? `Confirmer pour ${nbCoches} logement${nbCoches > 1 ? "s" : ""}` : "Enregistrer"}
        </button>
        <button type="button" onClick={() => { setOuvert(false); setEtape("saisie"); setMessage(null) }}
          style={{ fontSize: 12.5, border: `0.5px solid ${DS.border}`, borderRadius: DS.radiusControl, padding: "7px 14px", background: DS.card, cursor: "pointer" }}>
          Annuler
        </button>
      </div>
    </div>
  )
}

export default function TaxeParcPanel({ cfg, save, currentUser }) {
  // Un seul envoi à la fois pour tout l'écran (voir SaisieCommune). La ref
  // tranche les clics rapprochés avant même le prochain rendu ; l'état
  // redessine les boutons des autres communes.
  const verrouRef = useRef(false)
  const [occupe, setOccupe] = useState(false)
  const prendre = useCallback(() => {
    if (verrouRef.current) return false
    verrouRef.current = true
    setOccupe(true)
    return true
  }, [])
  const rendre = useCallback(() => { verrouRef.current = false; setOccupe(false) }, [])
  const verrou = useMemo(() => ({ occupe, prendre, rendre }), [occupe, prendre, rendre])
  const par = (currentUser && currentUser.email) || null
  const lignes = useMemo(() => apercuTaxeParc(cfg), [cfg])
  const groupes = useMemo(() => grouperParCommune(lignes), [lignes])
  const c = compterTaxeParc(lignes)
  const logsParId = useMemo(() => new Map(getAllLogsFlat(cfg).map((l) => [l.id, l])), [cfg])
  return (
    <Section
      tint="ardoise"
      icon={Landmark}
      title="Taxe de séjour — tout le parc"
      subtitle="Ce qui est appliqué aujourd'hui, logement par logement, regroupé par commune — et la saisie, commune par commune."
    >
      <FicheAlert tone="info" style={{ marginBottom: 14 }}>
        Les montants affichés sont <strong>ceux qui s'appliquent réellement</strong> aux devis en direct.
        Aucun montant n'est proposé ici : ils viennent des délibérations de chaque collectivité et de
        votre espace hébergeur.
      </FicheAlert>
      <div data-testid="taxe-parc-compteurs" style={{ fontSize: 13, color: DS.textSecondary, marginBottom: 12 }}>
        <strong>{c.avec}</strong> logement{c.avec > 1 ? "s" : ""} sur {c.total} avec un barème appliqué
        {c.sans > 0 ? <> · <strong>{c.sans}</strong> sans barème</> : null}
      </div>
      {groupes.map((g) => (
        <div key={g.cle} data-testid={`taxe-parc-commune-${g.cle}`} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: DS.marineDark, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {g.commune
              ? `${g.commune}${g.codes_postaux.length ? ` (${g.codes_postaux.join(", ")})` : ""}`
              : "Adresse illisible — commune inconnue"}
          </div>
          {!g.commune && (
            <div style={{ fontSize: 11.5, color: DS.textMuted, marginTop: 2 }}>
              L'adresse de la fiche doit se lire « rue, code postal ville » pour qu'on sache de quelle commune dépend le logement.
            </div>
          )}
          {g.logements.map((l) => <LigneLogement key={l.id} l={l} origine={origineBareme(logsParId.get(l.id))} />)}
          <SaisieCommune groupe={g} cfg={cfg} save={save} par={par} verrou={verrou} />
        </div>
      ))}
    </Section>
  )
}
